import type { OrderLine } from '../types'

/**
 * Sipariş Takip: which units of a line are still to be taken to the table.
 *
 * Counted in units, not lines, because a second round of the same item by
 * the same person merges into the existing line (2 ayran + 1 ayran = 3): the
 * line can be part served, part waiting.
 */

import { sameLine } from './lineMath'

/** Units served so far. A line without the field predates the feature: all served. */
export const servedOf = (l: OrderLine) => Math.min(l.qty, l.servedQty ?? l.qty)

/** Units still to be taken to the table. */
export const waitingQty = (l: OrderLine) => l.qty - servedOf(l)

/**
 * Stamps a ticket's lines as they are about to be saved: units added since
 * `prev` start waiting now; units that were already waiting keep their
 * older time; a line reduced below what was served is clamped.
 */
export function stampWaiting(prev: OrderLine[], next: OrderLine[], now = Date.now()): OrderLine[] {
  return next.map((line) => {
    const was = prev.find((p) => sameLine(p, line))
    const prevQty = was?.qty ?? 0
    const served = Math.min(line.qty, was ? servedOf(was) : 0)
    if (served >= line.qty) {
      const { waitingSince: _w, ...rest } = line
      return { ...rest, servedQty: line.qty }
    }
    const stillWaitingBefore = was && waitingQty(was) > 0 && was.waitingSince
    const since =
      line.qty > prevQty
        ? (stillWaitingBefore ? was!.waitingSince! : now)
        : (was?.waitingSince ?? now)
    return { ...line, servedQty: served, waitingSince: since }
  })
}

/**
 * Marks units of the lines matching `pick` as served, `count` of them, or
 * all. One tap on "Götürdüm" takes one plate: 2× Adana goes to 1×, then off
 * the list. The rest keep waiting from their original time.
 */
export function markServed(
  lines: OrderLine[],
  pick: (l: OrderLine) => boolean,
  count = Infinity,
): OrderLine[] {
  return lines.map((l) => {
    if (!pick(l) || waitingQty(l) === 0) return l
    const served = Math.min(l.qty, servedOf(l) + count)
    if (served < l.qty) return { ...l, servedQty: served }
    const { waitingSince: _w, ...rest } = l
    return { ...rest, servedQty: l.qty }
  })
}

/**
 * Two lines of the same item combining (merging tables, a second round):
 * served units add up, the older waiting time wins.
 */
export function combineServing(a: OrderLine, b: OrderLine): Pick<OrderLine, 'servedQty' | 'waitingSince'> {
  if (a.servedQty === undefined && b.servedQty === undefined) return {}
  const since = [a, b]
    .filter((l) => waitingQty(l) > 0 && l.waitingSince)
    .map((l) => l.waitingSince!)
  return {
    servedQty: servedOf(a) + servedOf(b),
    ...(since.length ? { waitingSince: Math.min(...since) } : {}),
  }
}
